/* Derived values — §5.1. Nothing here is stored; the whole view is recomputed
   from holdings, marks and rates on every pass. */

/* `limits` used to be read here for the ceilings and nothing else. They are
   records now, so the merged thresholds have no reader in this file. */
import type { Limits } from '../rules/limits';
import { bandById } from '../lib/capBands';
import { assignColors } from '../lib/colors';
import { laggingSymbol, oldestMarkAt } from './marks';
import { offHoursMarks, usableOffHours, type OffHoursMark } from './offHours';
import { coinOf, coinPerpSymbol, kindOf, quoteIsCrypto, type AssetKind } from './assetKind';
import { contractExposure, notionalByTicker, type ContractExposure } from './contractExposure';
import type {
  BookTotals, CapAssignment, CapBandRecord, CashBalance, CashFlow, Currency, FxRates, Holding,
  AccountRecord, MarketState, Perp, PerpMark, Position, Quote, Snapshot, ThemeRecord, Thesis,
} from '../types';

/* ---------- currency ---------- */

/** USD is the book's base. NBP quotes everything against PLN, so a non-PLN
    currency converts through it. Returns null when no rate is known, which the
    caller must surface rather than silently treating as zero. */
export function toUsd(amount: number, currency: string, fx: FxRates | undefined): number | null {
  const code = currency.toUpperCase();
  if (code === 'USD' || code === 'USDT') return amount;
  if (!fx || !fx.usdPln) return null;
  if (code === 'PLN') return amount / fx.usdPln;
  if (code === 'EUR') return fx.eurPln ? (amount * fx.eurPln) / fx.usdPln : null;
  if (code === 'HKD') return fx.hkdPln ? (amount * fx.hkdPln) / fx.usdPln : null;
  return null;
}

export function fromUsd(usd: number, currency: DisplayCode, fx: FxRates | undefined): number {
  if (currency === 'USD') return usd;
  return fx?.usdPln ? usd * fx.usdPln : usd;
}

export type DisplayCode = 'USD' | 'PLN';

/* The lines that can be drawn against the book.
 *
 * Here rather than on the settings screen because two screens offer them now:
 * settings, and the chart itself. One list, so a benchmark added is offered in
 * both places and neither can drift into showing a symbol the other does not.
 *
 * Funds rather than index symbols on purpose — `^GSPC` is not something the
 * price upstream answers for consistently, and what matters here is the shape,
 * which a tracker gives faithfully. */
export const BENCHMARKS: ReadonlyArray<{ id: string; label: string; short: string }> = [
  { id: 'SPY', label: 'S&P 500', short: 'S&P' },
  { id: 'QQQ', label: 'Nasdaq 100', short: 'Nasdaq' },
  { id: 'IWM', label: 'Russell 2000', short: 'Russell' },
  { id: 'GLD', label: 'Gold', short: 'Gold' },
];

/** Which comparison lines to draw, from settings.
 *
 * The setting used to be one symbol and is now a list. A book saved by the
 * older version still carries the single field, so it is read as a list of one
 * — migrated on read rather than by a database version, because the whole
 * change is one field and a migration that runs on every device to move one
 * string is more machinery than the problem. */
export function benchmarksFrom(settings: { benchmarks?: string[]; benchmark?: string }): string[] {
  if (Array.isArray(settings.benchmarks)) return settings.benchmarks.filter((s) => s !== '');
  return settings.benchmark ? [settings.benchmark] : [];
}

/* ---------- market session ---------- */

export interface SessionInfo {
  state: MarketState;
  label: 'OPEN' | 'PRE-MARKET' | 'AFTER HOURS' | 'CLOSED';
  extended: boolean;
}

const AWAKE: Record<MarketState, number> = { REGULAR: 4, PRE: 3, POST: 2, POSTPOST: 1, CLOSED: 0 };

/** Where one listing stands now.
 *
 * §9.1 says to read the session from the upstream rather than from a clock, so
 * that holidays take care of themselves — and that still holds. What does not is
 * trusting one field: the chart endpoint's `marketState` reads CLOSED for the
 * whole after-hours session, so at half past six in New York the app announced a
 * shut market while the same response carried the exchange's own post window
 * saying otherwise.
 *
 * So both are read, and the more awake of the two wins. The windows can correct
 * a session the field slept through; they can never shut a market the field says
 * is open, which is the direction where being wrong would matter. */
export function stateOf(quote: Quote, now = Date.now()): MarketState {
  const s = quote.sessions;
  if (!s) return quote.marketState;

  const t = Math.floor(now / 1000);
  const inside = (w?: [number, number]) => !!w && t >= w[0] && t < w[1];
  const fromWindows: MarketState = inside(s.regular) ? 'REGULAR'
    : inside(s.pre) ? 'PRE'
    : inside(s.post) ? 'POST'
    : 'CLOSED';

  return AWAKE[fromWindows] > AWAKE[quote.marketState] ? fromWindows : quote.marketState;
}

/* The book's session is the most awake state across its holdings — one open
 * listing means the book is live.
 *
 * Coins are left out of it, and that is not a detail. A coin trades every hour
 * of every day, so one in the book would report the session as OPEN at three in
 * the morning — and the session is not only a badge. It is what tells the app a
 * listing is shut and may be priced from MEXC's index, and it sets how often to
 * ask for marks. A book permanently OPEN would turn the first off silently and
 * hold the second at its fastest all night. What a coin is doing has no bearing
 * on whether New York is trading, which is the question this answers. */
export function bookSession(quotes: Quote[], now = Date.now()): SessionInfo {
  let best: MarketState = 'CLOSED';
  for (const q of quotes) {
    if (quoteIsCrypto(q)) continue;
    const state = stateOf(q, now);
    if (AWAKE[state] > AWAKE[best]) best = state;
  }
  return sessionOf(best);
}

export function sessionOf(state: MarketState): SessionInfo {
  switch (state) {
    case 'REGULAR': return { state, label: 'OPEN', extended: false };
    case 'PRE': return { state, label: 'PRE-MARKET', extended: true };
    case 'POST':
    case 'POSTPOST': return { state, label: 'AFTER HOURS', extended: true };
    default: return { state, label: 'CLOSED', extended: false };
  }
}

/* How often to ask for fresh marks, given where the session stands (§3.4).
 *
 * Kept here beside the session itself, and kept pure, because the one thing it
 * must never return while the market is shut is zero. The session is read off
 * the quotes; an app that stops fetching therefore cannot discover that the
 * market has opened, and stopping at CLOSED deadlocked it into carrying
 * overnight prices through the whole of the next pre-market. The pulse while
 * shut is slow enough to cost nothing and often enough to notice. */
export const REFRESH_MS = {
  regular: 60_000,
  extended: 5 * 60_000,
  shut: 15 * 60_000,
  backingOff: 60 * 60_000,
} as const;

export function refreshPeriodMs(
  state: MarketState,
  backingOff = false,
  /** True when the book holds something that trades while the exchanges do
      not — a coin. Shut then means shut for the listed half of the book only,
      and a quarter of an hour is a long time to leave the other half. */
  holdsAlwaysOpen = false,
): number {
  if (backingOff) return REFRESH_MS.backingOff;   // the retry path drives the cadence instead
  switch (state) {
    case 'REGULAR': return REFRESH_MS.regular;
    case 'PRE':
    case 'POST': return REFRESH_MS.extended;
    default: return holdsAlwaysOpen ? REFRESH_MS.extended : REFRESH_MS.shut;
  }
}

/** When the next session window opens or closes, across every listing held.
 *
 * The quotes carry the exchange's own windows for the day, so the moment
 * pre-market starts is a figure the app already has rather than something to
 * discover by asking again and again. Null when no window lies ahead — a book
 * of quotes fetched yesterday knows nothing about today's boundaries, and
 * guessing them from the clock is what §9.1 rules out. */
export function nextBoundaryMs(quotes: Quote[], now = Date.now()): number | null {
  const t = Math.floor(now / 1000);
  let soonest: number | null = null;

  for (const q of quotes) {
    for (const window of [q.sessions?.pre, q.sessions?.regular, q.sessions?.post]) {
      if (!window) continue;
      for (const edge of window) {
        if (edge <= t) continue;
        if (soonest === null || edge < soonest) soonest = edge;
      }
    }
  }
  return soonest === null ? null : (soonest - t) * 1000;
}

/** How long to wait before looking again: the cadence for the session we are
 *  in, cut short when a window opens sooner than that.
 *
 *  Without the second half the app finds out that pre-market started whenever
 *  its slow pulse next happens to fire, which is up to a quarter of an hour of
 *  looking at yesterday's price while the market is trading. The exchange's own
 *  boundary is in the data; there is no reason to discover it by accident.
 *
 *  A second is added so the wake lands inside the new window rather than on its
 *  edge, and the whole thing is floored so a boundary that has just passed
 *  cannot spin the timer. */
export function nextRefreshMs(
  state: MarketState,
  quotes: Quote[],
  backingOff = false,
  now = Date.now(),
  holdsAlwaysOpen = false,
): number {
  const period = refreshPeriodMs(state, backingOff, holdsAlwaysOpen);
  if (backingOff) return period;   // a failing upstream is not helped by punctuality

  const boundary = nextBoundaryMs(quotes, now);
  if (boundary === null) return period;
  return Math.max(1_000, Math.min(period, boundary + 1_000));
}

/** The mark to value the book at: the extended-hours print when one is trading,
    otherwise the contract's index while the exchange is shut, otherwise the
    regular close. The regular close stays available alongside so the view can
    show both (§9.1) — and so the record can be written from it. */
export function effectiveMark(
  q: Quote,
  /** MEXC's index for the same company, when the board carries one. */
  offHours?: OffHoursMark | undefined,
): { price: number; extended: number | null; offHours: OffHoursMark | null } {
  if (q.marketState === 'PRE' && typeof q.preMarketPrice === 'number') {
    return { price: q.preMarketPrice, extended: q.preMarketPrice, offHours: null };
  }
  if ((q.marketState === 'POST' || q.marketState === 'POSTPOST') && typeof q.postMarketPrice === 'number') {
    return { price: q.postMarketPrice, extended: q.postMarketPrice, offHours: null };
  }

  /* Nothing is quoting the share itself. The contract on MEXC is, and it is
     reported as an extended print rather than as the price — which is what
     keeps it out of the value timeline, exactly as a pre-market print is. */
  if (offHours && usableOffHours(q, stateOf(q))) {
    return { price: offHours.price, extended: offHours.price, offHours };
  }

  return { price: q.price, extended: null, offHours: null };
}

/* ---------- perps ---------- */

export interface PerpView extends Perp {
  mark: number | null;
  unrealisedUsd: number;
  returnOnMarginPct: number;
  /** Contracts held, and what they are worth at the current mark. The latter is
      the number MEXC prints as "Size (USDT)", so the card can be reconciled
      against the exchange screen. */
  contracts: number;
  notionalNowUsd: number;
  /** 0 = at the liquidation price, 1 = entry or better. */
  distanceToLiq: number;
  distanceToLiqPct: number;
  markStale: boolean;
  fetchedAt?: string;
}

export function derivePerps(perps: Perp[], marks: PerpMark[]): PerpView[] {
  const bySymbol = new Map(marks.map((m) => [m.symbol, m]));
  return perps.map((p) => {
    const m = bySymbol.get(p.symbol);
    const mark = m ? m.fairPrice : null;
    const direction = p.side === 'long' ? 1 : -1;
    // (mark - entry) / entry * sizeUsdt, sign-flipped for shorts (§5.1). sizeUsdt
    // is the notional at entry, so this is contracts x (mark - entry) exactly.
    const unrealisedUsd = mark === null ? 0 : ((mark - p.entryPrice) / p.entryPrice) * p.sizeUsdt * direction;
    const contracts = p.entryPrice > 0 ? p.sizeUsdt / p.entryPrice : 0;
    const notionalNowUsd = mark === null ? p.sizeUsdt : contracts * mark;
    const returnOnMarginPct = p.marginUsdt ? (unrealisedUsd / p.marginUsdt) * 100 : 0;

    // How much room is left before liquidation, as a fraction of the entry-to-liq gap.
    const span = Math.abs(p.entryPrice - p.liqPrice);
    const room = mark === null ? span : Math.abs(mark - p.liqPrice) * (
      p.side === 'long' ? (mark > p.liqPrice ? 1 : 0) : (mark < p.liqPrice ? 1 : 0)
    );
    const distanceToLiq = span > 0 ? Math.max(0, Math.min(1.5, room / span)) : 0;
    const distanceToLiqPct = mark === null || !mark ? 0 : Math.abs((mark - p.liqPrice) / mark) * 100;

    const view: PerpView = {
      ...p, mark, unrealisedUsd, returnOnMarginPct, distanceToLiq, distanceToLiqPct,
      contracts, notionalNowUsd, markStale: !m,
    };
    if (m) view.fetchedAt = m.fetchedAt;
    return view;
  });
}

/* ---------- positions ---------- */

export interface PositionExtras {
  regularPrice: number;
  extendedPrice: number | null;
  /** Set when the extended figure is a contract's index rather than a print on
      the listing itself, so the screen can name a different venue. */
  offHoursFrom: string | null;
  previousClose: number;
  dayChangeUsd: number;
  /** true when some lots carry no recorded cost basis (§10 IBKR). */
  costIncomplete: boolean;
  /** No mark arrived, so the position is being carried at its cost basis. */
  valuedAtCost: boolean;
  sharesWithCost: number;
  priceMissing: boolean;
  accounts: string[];
  riskToBookPct: number;
  /* ---- the same company, held with leverage as well ----
     Zero for the twelve names in this book that have no contract, which is why
     these read as ordinary numbers rather than as optional ones. See
     `state/contractExposure.ts` for why notional is the figure and why `weight`
     above is deliberately left counting shares alone. */
  /* The ceiling, resolved once. All three are null together — and they are
     null both when nothing is assigned and when what is assigned no longer
     exists. Those two are told apart by `capKind`, which stays whatever the
     assignment says: a non-null `capKind` beside a null `cap` is a position
     pointing at a ceiling the book no longer has, and the screen says so
     rather than reporting it as uncapped. */
  capName: string | null;
  capReason: string | null;
  /** Signed notional of the open contracts on this company. */
  contractNotionalUsd: number;
  /** How many, so a screen can say "a contract" or "two contracts". */
  contractCount: number;
  /** Shares plus contracts: what the book is actually exposed to here. */
  exposureUsd: number;
  /** That, against the book's total. Equal to `weight` when nothing is
      leveraged, which is the usual case. */
  exposureWeight: number;
  /** Coin or company. Drives the unit word and which sections a screen has any
      business drawing — see `state/assetKind.ts`. */
  assetKind: AssetKind;
  /** The MEXC contract a coin's price came from, when Yahoo does not list it.
      Kept apart from `offHoursFrom`: that one means "the exchange is shut and
      this stands in", which is a sentence about a company and never true of a
      coin. */
  coinPricedFrom: string | null;
}

export type FullPosition = Position & PositionExtras;

export interface DeriveInput {
  holdings: Holding[];
  quotes: Quote[];
  theses: Thesis[];
  themes: ThemeRecord[];
  caps: CapAssignment[];
  /** The ceilings themselves. Required: an optional list that silently
      supplied all nine was the shape that let a missing one read as no
      ceiling at all. */
  capBands: CapBandRecord[];
  cash: CashBalance[];
  perps: Perp[];
  perpMarks: PerpMark[];
  cashflows: CashFlow[];
  snapshots: Snapshot[];
  fx: FxRates | undefined;
  limits?: Limits;
  /** The broker records, so a futures wallet can be told from a share account.
      Absent falls back to counting no account as one, which reports the
      leveraged wallet as its margin alone — the old behaviour. */
  accountRecords?: AccountRecord[];
  /** Whether a shut exchange may be priced from the company's MEXC contract
      index. Default on; off restores the old behaviour of holding the last
      regular close until the exchange reopens. */
  useOffHoursMarks?: boolean;
}

export interface Book {
  positions: FullPosition[];
  /** Snapshots with contributed capital recomputed from the cash-flow record. */
  timeline: Snapshot[];
  totals: BookTotals;
  perps: PerpView[];
  /** One row per company carrying open contracts, biggest bet first. Empty in a
      book with no leverage. A row can name a ticker with no shareholding. */
  contractExposure: ContractExposure[];
  themes: ThemeExposure[];
  session: SessionInfo;
  cashByAccount: CashBalance[];
  /** Oldest mark among the symbols held — drives the "prices from 2h ago" line
      (§3.3). Marks for symbols nothing asks about any more are left out; they
      can never refresh, so they only make a fresh book look stale. */
  marksAsOf: string | undefined;
  /** The one listing dragging that figure back, when a single one is. */
  marksLaggard: string | undefined;
  anyPriceMissing: boolean;
}

export interface ThemeExposure {
  name: string;
  tickers: string[];
  weight: number;
  valueUsd: number;
  /** The same theme counting the contracts its members carry. Equal to `weight`
      unless one of them is leveraged. A theme can name a ticker the book holds
      only through a contract, and that ticker counts here and nowhere else —
      there is no shareholding for it to have been counted in. */
  exposureWeight: number;
  exposureUsd: number;
}

/** Money paid in as of a date — §5.1 converts each flow at the rate that applied
    on its own date, which is captured when the flow is entered. Passing no date
    totals everything. */
export function contributedAt(
  cashflows: CashFlow[],
  at: string | undefined,
  fx: FxRates | undefined,
): number {
  const cutoff = at ? new Date(at).getTime() : Infinity;
  return cashflows.reduce((sum, f) => {
    if (new Date(f.at).getTime() > cutoff) return sum;
    const usd = f.rateToUsd !== undefined
      ? f.amount * f.rateToUsd
      : toUsd(f.amount, f.currency, fx) ?? 0;
    return sum + usd;
  }, 0);
}

/** The chart's series. A snapshot stores the contributed figure that was known
    when it was written, which for a hand-seeded point is nothing. Once cash
    flows exist they are authoritative for every date, including dates that
    predate them being typed in — so the "money paid in" line and the gains-only
    mode fill in retroactively instead of staying blank forever. */
export function buildTimeline(
  snapshots: Snapshot[],
  cashflows: CashFlow[],
  fx: FxRates | undefined,
): Snapshot[] {
  if (cashflows.length === 0) return snapshots;
  return snapshots.map((s) => ({ ...s, contributedUsd: contributedAt(cashflows, s.at, fx) }));
}

/* Whether this moment is worth writing into the record.
 *
 * A book carrying part of itself at cost is not a measurement of the book. The
 * seed ships marks for three of twelve names, so a snapshot taken the instant
 * those three load records the other nine at cost — a drop of thousands that no
 * market produced, planted permanently at the right-hand end of every chart.
 * A missing point can be filled in later; a false one gets believed. */
export function worthRecording(
  totals: Pick<BookTotals, 'totalUsd' | 'unpricedCount'>,
  refreshing: boolean,
): boolean {
  if (refreshing) return false;
  if (totals.totalUsd <= 0) return false;
  return totals.unpricedCount === 0;
}

export function deriveBook(input: DeriveInput): Book {
  const { holdings, quotes, theses, themes, caps, cash, perps, perpMarks, cashflows, snapshots, fx } = input;

  const quoteBySymbol = new Map(quotes.map((q) => [q.symbol, q]));
  /* One pass over the MEXC board for the companies held, so a shut exchange
     does not mean a frozen book. Off when the setting says so. */
  const offHours = input.useOffHoursMarks === false
    ? new Map<string, OffHoursMark>()
    : offHoursMarks(holdings.map((h) => h.ticker), perpMarks);
  /* MEXC's index for a coin, keyed by ticker. Unlike the off-hours map above
     this is not a stand-in for a shut exchange — it is the only price there is
     for a coin Yahoo does not carry, and it is used whenever Yahoo has none. */
  const coinIndex = new Map(perpMarks.map((m) => [m.symbol, m]));
  const thesisByTicker = new Map(theses.map((t) => [t.ticker, t]));
  const capByTicker = new Map(caps.map((c) => [c.ticker, c.capKind]));

  // Group the stored lots into one position per ticker (§5, Position is derived).
  const byTicker = new Map<string, Holding[]>();
  for (const h of holdings) {
    const list = byTicker.get(h.ticker);
    if (list) list.push(h);
    else byTicker.set(h.ticker, [h]);
  }

  type Draft = FullPosition;
  const drafts: Draft[] = [];

  for (const [ticker, lots] of byTicker) {
    const first = lots[0]!;
    const quote = quoteBySymbol.get(first.yahooSymbol);
    const marks = quote ? effectiveMark(quote, offHours.get(ticker.toUpperCase())) : null;

    /* A coin Yahoo will not quote, priced off MEXC's board instead.
       *
       * Only where there is no quote at all: where Yahoo answers, its figure is
       * the one with a previous close and two years of daily history behind it,
       * and swapping between two sources tick by tick would put a difference
       * between venues into the day's change. The contract's index is the same
       * reading already trusted for a company whose exchange is shut. */
    const kind = kindOf(first, quote);
    const coinMark = kind === 'crypto' && !quote
      ? coinIndex.get(coinPerpSymbol(coinOf(first.yahooSymbol))) ?? null
      : null;
    const coinPrice = coinMark && coinMark.indexPrice > 0 ? coinMark.indexPrice : null;

    const totalShares = lots.reduce((s, h) => s + h.shares, 0);
    if (totalShares === 0) continue;

    // Lots without a recorded cost are carried but excluded from cost basis and
    // return, so an unknown never masquerades as a zero-cost gain.
    const costed = lots.filter((h) => h.avgCost > 0);
    const sharesWithCost = costed.reduce((s, h) => s + h.shares, 0);
    const avgCost = sharesWithCost > 0
      ? costed.reduce((s, h) => s + h.shares * h.avgCost, 0) / sharesWithCost
      : 0;

    // A MEXC index is quoted in USDT, which this book carries as dollars.
    const listingCurrency = (quote?.currency ?? (coinPrice !== null ? 'USD' : first.currency)) as Currency;
    const costBasis = toUsd(sharesWithCost * avgCost, first.currency, fx) ?? 0;

    /* No mark yet — a cold start with no network, or a symbol the upstream does
       not answer for. Valuing the lot at zero would be the worst possible
       answer: it silently shrinks the book, throws every weight off, and makes
       the drawdown check scream. Cost is a real recorded number, so the position
       is carried at cost and flagged, and the UI says "at cost" instead of
       showing a price it does not have. */
    const valuedAtCost = !quote && coinPrice === null && avgCost > 0;
    const price = marks?.price ?? coinPrice ?? (valuedAtCost ? avgCost : 0);
    const marketValue = toUsd(totalShares * price, listingCurrency, fx) ?? 0;
    const knownValue = toUsd(sharesWithCost * price, listingCurrency, fx) ?? 0;

    const previousClose = quote?.previousClose ?? 0;   // 0 when no mark: no day change to show
    const dayChangeUsd = quote
      ? toUsd(totalShares * (price - previousClose), listingCurrency, fx) ?? 0
      : 0;
    const dayChangePct = previousClose > 0 ? ((price - previousClose) / previousClose) * 100 : 0;

    const totalReturnUsd = costBasis > 0 ? knownValue - costBasis : 0;
    const totalReturnPct = costBasis > 0 ? (totalReturnUsd / costBasis) * 100 : 0;

    const capKind = capByTicker.get(ticker) ?? null;
    const band = bandById(input.capBands, capKind);
    const thesis = thesisByTicker.get(ticker);

    const draft: Draft = {
      ticker,
      yahooSymbol: first.yahooSymbol,
      holdings: lots,
      shares: totalShares,
      avgCost,
      currency: first.currency,
      price,
      marketValue,
      costBasis,
      weight: 0,             // filled once the book total is known
      dayChangePct,
      totalReturnPct,
      totalReturnUsd,
      themes: themes.filter((t) => t.tickers.includes(ticker)).map((t) => t.name),
      capKind,
      cap: band ? band.pct : null,
      capName: band ? band.name : null,
      capReason: band?.reason ?? null,
      color: '',             // assigned after sorting by weight
      regularPrice: quote?.price ?? 0,
      extendedPrice: marks?.extended ?? null,
      offHoursFrom: marks?.offHours?.symbol ?? null,
      previousClose,
      dayChangeUsd,
      costIncomplete: sharesWithCost < totalShares,
      valuedAtCost,
      sharesWithCost,
      priceMissing: !quote && coinPrice === null,
      assetKind: kind,
      coinPricedFrom: coinPrice !== null && coinMark ? coinMark.symbol : null,
      accounts: [...new Set(lots.map((h) => h.account))],
      riskToBookPct: 0,      // needs the weight
      contractNotionalUsd: 0, // needs the contracts, priced below
      contractCount: 0,
      exposureUsd: 0,
      exposureWeight: 0,
    };
    if (quote?.shortName) draft.name = quote.shortName;
    else if (first.name) draft.name = first.name;
    if (thesis) draft.thesis = thesis;
    if (quote) draft.quote = quote;
    drafts.push(draft);
  }

  /* ---- book total (§5.1) ---- */

  const equityUsd = drafts.reduce((s, p) => s + p.marketValue, 0);
  const cashUsd = cash.reduce((s, c) => s + (toUsd(c.amount, c.currency, fx) ?? 0), 0);
  /* Closed contracts are kept but stop counting: their margin came back to the
     wallet and their result is history, so leaving them in would double the one
     and keep marking the other to a price that no longer applies to anything. */
  const perpViews = derivePerps(perps.filter((p) => !p.closedAt), perpMarks);
  const perpMarginUsd = perpViews.reduce((s, p) => s + p.marginUsdt, 0);
  const perpUnrealisedUsd = perpViews.reduce((s, p) => s + p.unrealisedUsd, 0);

  /* The rest of the money sitting on the exchange.
   *
   * "Leveraged equity" was margin plus unrealised — what the open positions are
   * worth — and read as though that were everything on the venue. It is not:
   * whatever is not posted against a contract is still on the exchange, still
   * yours, and still the thing you would size the next position out of. It is
   * in the book's total either way, under cash; this only separates out how
   * much of that cash is sitting there. */
  const futuresIds = new Set(
    (input.accountRecords ?? []).filter((a) => a.isFutures).map((a) => a.id),
  );
  const futuresFreeUsd = cash
    .filter((c) => futuresIds.has(c.account))
    .reduce((s, c) => s + (toUsd(c.amount, c.currency, fx) ?? 0), 0);
  const totalUsd = equityUsd + cashUsd + perpMarginUsd + perpUnrealisedUsd;

  /* What is held with leverage, by the company it is held on. Notional at the
     mark, signed by side; see `state/contractExposure.ts` for both choices. */
  const exposureRows = contractExposure(perpViews);
  const notional = notionalByTicker(exposureRows);

  for (const p of drafts) {
    p.weight = totalUsd > 0 ? (p.marketValue / totalUsd) * 100 : 0;
    // riskOfPosition = weight * (lossIfWrong / 100)
    p.riskToBookPct = p.thesis ? p.weight * (p.thesis.lossIfWrong / 100) : 0;

    /* The shareholding and the contracts on the same name, read together. The
       weight above stays what it has always been: this is a second figure, not
       a correction to that one. */
    const row = exposureRows.find((r) => r.ticker === p.ticker.toUpperCase());
    p.contractNotionalUsd = row?.notionalUsd ?? 0;
    p.contractCount = row?.count ?? 0;
    p.exposureUsd = p.marketValue + p.contractNotionalUsd;
    p.exposureWeight = totalUsd > 0 ? (p.exposureUsd / totalUsd) * 100 : 0;
  }

  drafts.sort((a, b) => b.marketValue - a.marketValue);
  const colors = assignColors(drafts.map((p) => p.ticker));
  for (const p of drafts) p.color = colors.get(p.ticker) ?? '#5B6880';

  /* ---- themes: sum of member weights, deduplicated per position (§5.2) ---- */

  const weightByTicker = new Map(drafts.map((p) => [p.ticker, p]));
  /* A theme can name a company the book holds only through a contract. That
     ticker used to be filtered out of its theme entirely, because the filter
     asked whether there was a shareholding — so a leveraged bet contributed
     nothing to the limit written to govern exactly that kind of concentration.
     It is a member now, carrying no share weight and its full notional. */
  const themeExposure: ThemeExposure[] = themes.map((t) => {
    const members = [...new Set(t.tickers)].filter(
      (tk) => weightByTicker.has(tk) || notional.has(tk.toUpperCase()),
    );
    const exposureOf = (tk: string) => {
      const held = weightByTicker.get(tk);
      return held ? held.exposureUsd : notional.get(tk.toUpperCase()) ?? 0;
    };
    const exposureUsd = members.reduce((s, tk) => s + exposureOf(tk), 0);
    return {
      name: t.name,
      tickers: members,
      weight: members.reduce((s, tk) => s + (weightByTicker.get(tk)?.weight ?? 0), 0),
      valueUsd: members.reduce((s, tk) => s + (weightByTicker.get(tk)?.marketValue ?? 0), 0),
      exposureUsd,
      exposureWeight: totalUsd > 0 ? (exposureUsd / totalUsd) * 100 : 0,
    };
  }).sort((a, b) => b.weight - a.weight);

  /* ---- funding and history ---- */

  const contributedUsd = contributedAt(cashflows, undefined, fx);
  const marketAddedUsd = totalUsd - contributedUsd;
  const returnOnMoneyInPct = contributedUsd > 0 ? (marketAddedUsd / contributedUsd) * 100 : 0;

  // Peak is the running maximum of the measured series, including where it is now.
  const peakUsd = Math.max(
    totalUsd,
    ...snapshots.map((s) => s.totalUsd),
  );
  const drawdownPct = peakUsd > 0 ? ((peakUsd - totalUsd) / peakUsd) * 100 : 0;

  const costBasisUsd = drafts.reduce((s, p) => s + p.costBasis, 0);
  /* What the book is worth on regular-hours marks, alongside what it is worth
     right now.
   *
   * These differ only outside the session, and only because an extended-hours
   * print is being used. §9.1 says to show those prints — with a badge and a
   * caution, which the screen does — and it also says why: they come from thin
   * volume and often do not survive to the open. A price worth showing is not
   * automatically a price worth writing into the permanent record, and the
   * record is the one thing that cannot be re-derived later. So the screen keeps
   * the live figure and the history takes this one. During the session they are
   * the same number. */
  const extendedLiftUsd = drafts.reduce((sum, p) => {
    if (p.priceMissing || p.extendedPrice === null || p.regularPrice <= 0) return sum;
    const difference = (p.price - p.regularPrice) * p.shares;
    return sum + (toUsd(difference, p.currency, fx) ?? 0);
  }, 0);

  const dayChangeUsd = drafts.reduce((s, p) => s + p.dayChangeUsd, 0);
  const previousTotal = totalUsd - dayChangeUsd;

  const totals: BookTotals = {
    equityUsd,
    cashUsd,
    perpMarginUsd,
    perpUnrealisedUsd,
    futuresFreeUsd,
    futuresWalletUsd: perpMarginUsd + perpUnrealisedUsd + futuresFreeUsd,
    totalUsd,
    totalAtRegularUsd: totalUsd - extendedLiftUsd,
    costBasisUsd,
    dayChangeUsd,
    dayChangePct: previousTotal > 0 ? (dayChangeUsd / previousTotal) * 100 : 0,
    contributedUsd,
    marketAddedUsd,
    returnOnMoneyInPct,
    cashPct: totalUsd > 0 ? (cashUsd / totalUsd) * 100 : 0,
    equityPct: totalUsd > 0 ? (equityUsd / totalUsd) * 100 : 0,
    peakUsd,
    drawdownPct,
    unpricedCount: drafts.filter((p) => p.priceMissing).length,
    unpricedValueUsd: drafts.filter((p) => p.priceMissing).reduce((sum, p) => sum + p.marketValue, 0),
  };


  return {
    positions: drafts,
    timeline: buildTimeline(snapshots, cashflows, fx),
    totals,
    perps: perpViews,
    contractExposure: exposureRows,
    themes: themeExposure,
    session: bookSession(quotes),
    cashByAccount: cash,
    /* Only the marks this total is built from. A quote left behind by a
       company no longer held cannot be refreshed and must not report on the
       freshness of the book's value. */
    marksAsOf: oldestMarkAt(quotes, holdings.map((h) => h.yahooSymbol)),
    marksLaggard: laggingSymbol(quotes, holdings.map((h) => h.yahooSymbol)),
    anyPriceMissing: drafts.some((p) => p.priceMissing),
  };
}

/** The exact share count that brings a position down to its ceiling (§7.4,
    "To your limit"). Returns 0 when the position is already inside it.

    The proceeds stay inside the book — a sale here moves value from a position
    into cash, it does not withdraw it — so the denominator does not move. After
    selling `x` shares the weight is (value - x·price) / total, and setting that
    equal to the cap gives the count directly. */
export function sharesToReachCap(
  positionValueUsd: number,
  bookTotalUsd: number,
  priceUsd: number,
  capPct: number,
): number {
  if (priceUsd <= 0 || bookTotalUsd <= 0) return 0;
  const cap = capPct / 100;
  if (positionValueUsd / bookTotalUsd <= cap) return 0;
  const valueToSell = positionValueUsd - cap * bookTotalUsd;
  return Math.max(0, valueToSell / priceUsd);
}
